import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shoppingApi, type ShoppingSession, type SessionStatus } from '../api/shopping';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { SessionBadge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonList } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { formatDate, formatCurrency, normalizeText } from '../utils/format';
import { PlusIcon } from '../components/ui/icons';

function HistoryCard({
  session,
  onClick,
  onRepeat,
}: {
  session: ShoppingSession;
  onClick: () => void;
  onRepeat: () => void;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        className="p-4 cursor-pointer active:bg-gray-50 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-bold text-gray-900 text-base truncate">{session.storeName}</p>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{formatDate(session.startedAt)}</p>
            <p className="text-sm text-gray-600 mt-1">
              {session.itemCount ?? '—'} itens
              {session.total != null && session.total > 0
                ? ` · ${formatCurrency(session.total)}`
                : ''}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <SessionBadge status={session.status} />
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-100 flex">
        <button
          onClick={onClick}
          className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors touch-manipulation"
        >
          Detalhes
        </button>
        <div className="w-px bg-gray-100" />
        <button
          onClick={(e) => { e.stopPropagation(); onRepeat(); }}
          className="flex-1 py-3 text-sm font-medium text-emerald-600 hover:bg-emerald-50 transition-colors touch-manipulation"
        >
          Repetir
        </button>
      </div>
    </div>
  );
}

export function HistoryScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SessionStatus | 'ALL'>('ALL');

  const { data: finished, isLoading: loadingFinished } = useQuery({
    queryKey: ['shopping', 'history', 'FINISHED'],
    queryFn: () => shoppingApi.listSessions({ status: 'FINISHED' }),
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const { data: cancelled, isLoading: loadingCancelled } = useQuery({
    queryKey: ['shopping', 'history', 'CANCELLED'],
    queryFn: () => shoppingApi.listSessions({ status: 'CANCELLED' }),
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const isLoading = loadingFinished || loadingCancelled;

  const allSessions = useMemo(() => {
    let sessions = [...(finished ?? []), ...(cancelled ?? [])].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );

    if (statusFilter !== 'ALL') {
      sessions = sessions.filter((s) => s.status === statusFilter);
    }

    if (search.trim()) {
      const normalized = normalizeText(search);
      sessions = sessions.filter((s) =>
        normalizeText(s.storeName).includes(normalized)
      );
    }

    return sessions;
  }, [finished, cancelled, search, statusFilter]);

  const repeatMutation = useMutation({
    mutationFn: (sessionId: string) => shoppingApi.repeatSession(sessionId),
    onSuccess: (newSession) => {
      queryClient.setQueryData(['shopping', 'active'], newSession);
      queryClient.invalidateQueries({ queryKey: ['shopping'] });
      navigate('/shopping/active');
      addToast(`Nova compra em ${newSession.storeName} criada!`, 'success');
    },
    onError: (err: { code?: string }) => {
      if (err.code === 'ACTIVE_SESSION_EXISTS') {
        addToast('Já existe uma compra em andamento.', 'error');
      } else {
        addToast('Erro ao repetir compra.', 'error');
      }
    },
  });

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title="Histórico de Compras" />

      <div className="flex-1 px-4 py-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder="Buscar por loja..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-gray-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        {/* Status filter */}
        <div className="flex gap-2">
          {(['ALL', 'FINISHED', 'CANCELLED'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={[
                'flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors touch-manipulation',
                statusFilter === status
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-emerald-300',
              ].join(' ')}
            >
              {status === 'ALL' ? 'Todas' : status === 'FINISHED' ? 'Finalizadas' : 'Canceladas'}
            </button>
          ))}
        </div>

        {isLoading ? (
          <SkeletonList count={4} />
        ) : allSessions.length === 0 ? (
          <EmptyState
            icon={
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#f3f4f6" />
                <path
                  d="M20 20h24M20 32h24M20 44h16"
                  stroke="#9ca3af"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx="46" cy="44" r="6" fill="#d1fae5" stroke="#10b981" strokeWidth="2" />
                <path d="M43.5 44l2 2 3-3" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            title={
              search.trim()
                ? 'Nenhuma compra encontrada'
                : statusFilter === 'CANCELLED'
                ? 'Não existem compras canceladas até o momento'
                : statusFilter === 'FINISHED'
                ? 'Nenhuma compra finalizada ainda'
                : 'Nenhuma compra no histórico'
            }
            description={
              search.trim()
                ? 'Tente outro termo de busca.'
                : statusFilter === 'CANCELLED'
                ? 'Compras canceladas por aqui aparecerão nesta lista.'
                : 'Suas compras finalizadas aparecerão aqui.'
            }
            action={
              search.trim()
                ? undefined
                : { label: 'Iniciar compra', onClick: () => navigate('/shopping'), icon: <PlusIcon size={16} /> }
            }
          />
        ) : (
          allSessions.map((session) => (
            <HistoryCard
              key={session.id}
              session={session}
              onClick={() => navigate(`/history/${session.id}`)}
              onRepeat={() => repeatMutation.mutate(session.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
